import { useState, useCallback, useEffect } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useGetTopicFitRecommendationsQuery, useApplyRecommendationsMutation } from '../../__generated__/chirp/com/hubspot/ai/engine/optimization/rpc/AiEngineOptimizationService.hooks';

/**
 * Custom hook that notifies parent when incomplete recommendation count changes
 */
import { jsx as _jsx } from "react/jsx-runtime";
const useNotifyIncompleteCount = (fitRecommendations, localOverrides, onIncompleteCountChange) => {
  useEffect(() => {
    // skip if no callback provided or data still loading
    if (!onIncompleteCountChange || !(fitRecommendations !== null && fitRecommendations !== void 0 && fitRecommendations.recommendations)) return;

    // Calculate incomplete count (includes optimistic updates from user interactions)
    const count = fitRecommendations.recommendations.filter(rec => {
      var _localOverrides$get;
      const currentApplied = (_localOverrides$get = localOverrides.get(rec.id)) !== null && _localOverrides$get !== void 0 ? _localOverrides$get : rec.applied;
      return !currentApplied;
    }).length;

    // Notify parent with latest count
    onIncompleteCountChange(count);
  }, [fitRecommendations, localOverrides, onIncompleteCountChange]);
};

/**
 * Custom hook for managing recommendation checkboxes with optimistic updates
 */
const useRecommendationCheckboxes = contentId => {
  const {
    data: fitRecommendations,
    refetch
  } = useGetTopicFitRecommendationsQuery({
    variables: {
      contentId
    }
  });
  const [localOverrides, setLocalOverrides] = useState(() => new Map());
  const [applyRecommendations] = useApplyRecommendationsMutation();
  const handleRecommendationChange = useCallback((recommendationId, currentApplied) => {
    const newApplied = !currentApplied;

    // Optimistic update
    setLocalOverrides(prev => new Map(prev.set(recommendationId, newApplied)));
    applyRecommendations({
      variables: {
        contentId,
        applyRecommendationIds: newApplied ? [recommendationId] : [],
        unapplyRecommendationIds: newApplied ? [] : [recommendationId]
      }
    }).then(() => {
      // Refetch to keep cache fresh for other components
      refetch().catch(() => {
        // Ignore refetch errors - not critical for UX
      });
    }).catch(error => {
      // Revert on error
      setLocalOverrides(prev => new Map(prev.set(recommendationId, currentApplied)));
      FloatingAlertStore.addAlert({
        type: 'danger',
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "seo-tools-lib.contentRecommendations.error.updateRecommendationStatus"
        })
      });
      console.error('Failed to update recommendation:', error);
    });
  }, [applyRecommendations, contentId, refetch]);
  const getCurrentAppliedState = useCallback((recommendationId, serverApplied) => {
    var _localOverrides$get2;
    return (_localOverrides$get2 = localOverrides.get(recommendationId)) !== null && _localOverrides$get2 !== void 0 ? _localOverrides$get2 : serverApplied;
  }, [localOverrides]);
  return {
    fitRecommendations,
    handleRecommendationChange,
    getCurrentAppliedState,
    localOverrides
  };
};
/**
 * Reusable component that displays AI search content recommendations as checkboxes with optimistic updates
 */
const AISearchContentRecommendations = ({
  contentId,
  onIncompleteCountChange
}) => {
  const {
    fitRecommendations,
    handleRecommendationChange,
    getCurrentAppliedState,
    localOverrides
  } = useRecommendationCheckboxes(contentId);
  useNotifyIncompleteCount(fitRecommendations, localOverrides, onIncompleteCountChange);
  if (!(fitRecommendations !== null && fitRecommendations !== void 0 && fitRecommendations.recommendations) || fitRecommendations.recommendations.length === 0) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "seo-tools-lib.contentRecommendations.emptyState"
    });
  }
  return /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    gap: "400",
    marginBlockEnd: "400",
    children: fitRecommendations.recommendations.map(recommendation => {
      const currentAppliedState = getCurrentAppliedState(recommendation.id, recommendation.applied);
      return /*#__PURE__*/_jsx(Checkbox, {
        checked: currentAppliedState,
        onChange: () => handleRecommendationChange(recommendation.id, currentAppliedState // Use current state, not original server value
        ),
        children: /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          marginBlockStart: "-100",
          textDecoration: currentAppliedState ? 'line-through' : 'none',
          children: recommendation.recommendation
        })
      }, recommendation.id);
    })
  });
};
export default AISearchContentRecommendations;